import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ClipboardList, ExternalLink, ArrowLeft, Loader2, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { PublicHeader } from "@/components/PublicHeader";
import { useI18n } from "@/contexts/I18nContext";
import { useMember } from "@/contexts/MemberContext";
import { api } from "@/lib/api";

/**
 * Public survey page.
 *
 * Embeds the admin-configured survey URL in an iframe and always offers an
 * "open in browser" CTA as a fallback for providers that disallow framing
 * via X-Frame-Options / CSP frame-ancestors (which we cannot detect from
 * client JS due to cross-origin restrictions — so we surface the fallback
 * unconditionally rather than guessing).
 */
export default function SurveyPage() {
  const { t, lang } = useI18n();
  const { member } = useMember();
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [iframeLoaded, setIframeLoaded] = useState(false);
  const [completedAt, setCompletedAt] = useState(null);
  const [markingComplete, setMarkingComplete] = useState(false);
  const iframeRef = useRef(null);
  const loggedOpenRef = useRef(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get("/settings");
        if (!cancelled) setSettings(data);
      } catch (err) {
        // Best-effort; the UI falls back to "no active survey" message
        if (!cancelled) console.error("SurveyPage: failed to load settings", err);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // Fetch this member's current survey status (to hide "I finished" button
  // if they already confirmed completion).
  useEffect(() => {
    if (!member?.id) return;
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get(`/survey/me`, { params: { member_id: member.id } });
        if (!cancelled) setCompletedAt(data?.completed_at || null);
      } catch (_) { /* ignore */ }
    })();
    return () => { cancelled = true; };
  }, [member?.id]);

  // Auto-log "opened" once per SurveyPage mount for the current member.
  useEffect(() => {
    if (loggedOpenRef.current) return;
    if (!member?.id) return;
    if (loading) return;
    if (!(settings?.survey_enabled && (settings?.survey_url || "").trim())) return;
    loggedOpenRef.current = true;
    api.post("/survey/open", { member_id: member.id }).catch(() => { /* ignore */ });
  }, [member?.id, loading, settings]);

  const url = (settings?.survey_url || "").trim();
  const enabled = !!settings?.survey_enabled && !!url;
  const title = enabled
    ? ((lang === "en" ? settings.survey_title_en : settings.survey_title_es)
        || settings.survey_title_es || settings.survey_title_en)
    : t.survey.pageTitle;
  const description = enabled
    ? ((lang === "en" ? settings.survey_description_en : settings.survey_description_es)
        || "")
    : "";

  const openInBrowser = () => {
    if (!url) return;
    window.open(url, "_blank", "noopener,noreferrer");
  };

  const markCompleted = async () => {
    if (!member?.id || markingComplete) return;
    setMarkingComplete(true);
    try {
      const { data } = await api.post("/survey/complete", { member_id: member.id });
      setCompletedAt(data?.completed_at || new Date().toISOString());
      toast.success(lang === "es"
        ? "\u00a1Gracias! Marcamos tu encuesta como completada."
        : "Thank you! We marked your survey as completed.");
    } catch (err) {
      toast.error(lang === "es"
        ? "No pudimos guardar tu confirmacion. Intenta de nuevo."
        : "We could not save your confirmation. Please try again.");
    } finally {
      setMarkingComplete(false);
    }
  };

  return (
    <div>
      <PublicHeader subtitle={title || t.survey.pageTitle} />
      <motion.div
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.22 }}
        className="px-4 pt-4 space-y-3"
      >
        <div className="flex items-center justify-between gap-2 px-1">
          <Button asChild variant="ghost" size="sm" className="-ml-2" data-testid="survey-back-home">
            <Link to="/"><ArrowLeft className="mr-1 h-4 w-4" />{t.survey.backHome}</Link>
          </Button>
          {url && (
            <Button
              variant="outline"
              size="sm"
              onClick={openInBrowser}
              data-testid="survey-open-browser"
            >
              <ExternalLink className="mr-1 h-4 w-4" />
              {t.survey.openInBrowser}
            </Button>
          )}
        </div>

        {loading ? (
          <Skeleton className="h-[70vh] w-full rounded-2xl" />
        ) : !enabled ? (
          <Card className="rounded-2xl border-dashed bg-white/60">
            <CardContent className="py-14 text-center space-y-3" data-testid="survey-disabled">
              <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-2xl bg-primary/10 ring-1 ring-primary/20">
                <ClipboardList className="h-5 w-5 text-primary" />
              </div>
              <p className="text-sm text-muted-foreground">
                {lang === "en"
                  ? "There is no active survey right now."
                  : "No hay ninguna encuesta activa por ahora."}
              </p>
              <Button asChild variant="outline" size="sm">
                <Link to="/">{t.survey.backHome}</Link>
              </Button>
            </CardContent>
          </Card>
        ) : (
          <Card className="overflow-hidden rounded-2xl ring-1 ring-black/5 shadow-sm">
            <div className="p-3 border-b bg-muted/40">
              <p className="text-xs text-muted-foreground" data-testid="survey-hint">
                {t.survey.openInBrowserHint}
              </p>
              {description && (
                <p className="mt-1 text-sm text-foreground/90 leading-snug">{description}</p>
              )}
            </div>
            <div className="relative bg-white" style={{ minHeight: "70vh" }}>
              {!iframeLoaded && (
                <div className="absolute inset-0 flex flex-col items-center justify-center text-sm text-muted-foreground gap-2 pointer-events-none">
                  <Loader2 className="h-5 w-5 animate-spin text-primary" />
                  <span>{t.survey.loadingHint}</span>
                </div>
              )}
              <iframe
                ref={iframeRef}
                src={url}
                title="external-survey"
                className="block h-[78vh] w-full border-0"
                allow="clipboard-write; geolocation"
                referrerPolicy="no-referrer-when-downgrade"
                onLoad={() => setIframeLoaded(true)}
                data-testid="survey-iframe"
              />
            </div>
            <div className="border-t bg-muted/30 p-3 flex flex-wrap items-center justify-between gap-2">
              <p className="text-[11px] text-muted-foreground flex-1 min-w-[10rem]">{t.survey.thanksHint}</p>
              <div className="flex flex-wrap items-center gap-2">
                <Button
                  size="sm"
                  variant="outline"
                  onClick={openInBrowser}
                  data-testid="survey-open-browser-footer"
                >
                  <ExternalLink className="mr-1 h-4 w-4" />
                  {t.survey.openInBrowser}
                </Button>
                {completedAt ? (
                  <span
                    className="inline-flex items-center gap-1 rounded-full bg-emerald-50 px-3 py-1 text-xs font-medium text-emerald-700 ring-1 ring-emerald-200"
                    data-testid="survey-completed-badge"
                  >
                    <CheckCircle2 className="h-3.5 w-3.5" />
                    {lang === "es" ? "Completada" : "Completed"}
                  </span>
                ) : (
                  <Button
                    size="sm"
                    onClick={markCompleted}
                    disabled={markingComplete || !member?.id}
                    data-testid="survey-mark-completed"
                  >
                    {markingComplete
                      ? <Loader2 className="mr-1 h-4 w-4 animate-spin" />
                      : <CheckCircle2 className="mr-1 h-4 w-4" />}
                    {lang === "es" ? "Ya termine la encuesta" : "I finished the survey"}
                  </Button>
                )}
              </div>
            </div>
          </Card>
        )}
        <div className="h-2" />
      </motion.div>
    </div>
  );
}
